<script>
	import { base } from '$app/paths';
	import SeoHead from '../../../../common/SeoHead.svelte';
	import GuideImage from '../../../kdmgen/help/GuideImage.svelte';
	import guides from '../guides.json';
	import screenshots from '../screenshots.json';
	let { data } = $props();
	let guide = $derived(data.guide);
	let screenReferences = $derived(screenshots[guide.slug] || []);
	let overviewScreens = $derived(screenReferences.filter((item) => !item.step));
	const origin = `https://tools.d-cine.net${base}`;
	let structuredData = $derived(
		JSON.stringify({
			'@context': 'https://schema.org',
			'@graph': [
				{
					'@type': 'TechArticle',
					headline: guide.heading,
					description: guide.description,
					url: `${origin}/slate/help/${guide.slug}/`,
					inLanguage: 'en',
					image: `${origin}/slate-help/${guide.slug}-${guide.steps[0].id}.webp`,
					author: { '@type': 'Organization', name: 'D-Cine.net', url: 'https://tools.d-cine.net' },
					publisher: { '@type': 'Organization', name: 'D-Cine.net' },
					articleSection: 'Slate Tools user manuals',
					proficiencyLevel: 'Beginner',
					mainEntityOfPage: `${origin}/slate/help/${guide.slug}/`
				},
				{
					'@type': 'BreadcrumbList',
					itemListElement: [
						{
							'@type': 'ListItem',
							position: 1,
							name: 'Slate Tools',
							item: `${origin}/slate/welcome/`
						},
						{
							'@type': 'ListItem',
							position: 2,
							name: 'Walk Throughs/Help',
							item: `${origin}/slate/help/`
						},
						{
							'@type': 'ListItem',
							position: 3,
							name: guide.title,
							item: `${origin}/slate/help/${guide.slug}/`
						}
					]
				}
			]
		}).replace(/</g, '\\u003c')
	);
</script>

<SeoHead
	title={`${guide.title} ${guide.status ? 'availability' : 'guide'}`}
	description={guide.description}
	path={`/slate/help/${guide.slug}/`}
	image={`/slate-help/${guide.slug}-${guide.steps[0].id}.webp`}
	type="article"
/>
<svelte:head>{@html `<script type="application/ld+json">${structuredData}</script>`}</svelte:head>

<main class="help-content">
	<article>
		<header class="guide-header">
			<nav class="breadcrumbs" aria-label="Breadcrumb">
				<a href={`${base}/slate/welcome/`}>Slate Tools</a><span aria-hidden="true">/</span><a
					href={`${base}/slate/help/`}>Walk Throughs/Help</a
				><span aria-hidden="true">/</span><span>{guide.title}</span>
			</nav>
			<p class="guide-kicker">
				{guide.status
					? 'Planned tool · current availability'
					: `${guide.time} · ${guide.steps.length} illustrated steps`}
			</p>
			<h1>{guide.heading}</h1>
			<p class="guide-lead">{guide.intro}</p>
			<a class="open-tool" href={`${base}/slate/${guide.tool}/`} target="_blank" rel="noopener"
				><i class="bx bx-link-external" aria-hidden="true"></i>{guide.status
					? 'Open the development notice'
					: `Open ${guide.title}`}</a
			>
		</header>
		<div class="guide-layout">
			<aside class="guide-toc">
				<nav aria-label="On this page">
					<strong>On this page</strong><a href="#before-you-begin"
						>{guide.status ? 'Current availability' : 'Before you begin'}</a
					>{#if screenReferences.length}<a href="#screen-reference">Screen reference</a
						>{/if}{#each guide.steps as step, i}<a href={`#${step.id}`}
							><span>{i + 1}.</span> {step.title}</a
						>{/each}<a href="#troubleshooting"
						>{guide.status ? 'Common question' : 'Troubleshooting'}</a
					>
				</nav>
			</aside>
			<div class="guide-body">
				<section id="before-you-begin" class="before-you-begin">
					<h2>{guide.status ? 'Current availability' : 'Before you begin'}</h2>
					<ul>
						{#each guide.prerequisites as item}<li>{item}</li>{/each}
					</ul>
				</section>
				{#if screenReferences.length}
					<section id="screen-reference" class="screen-reference">
						<h2>Recognise the screen</h2>
						<p>
							These are cropped views of the actual tool. Numbered examples below explain the
							workflow with fictional records; screenshots are not a promise of current release
							dates or scores.
						</p>
						{#each overviewScreens as screenshot, i}<GuideImage
								{...screenshot}
								directory="slate-help"
								eager={i === 0}
							/>{/each}
					</section>
				{/if}
				{#each guide.steps as step, i}
					<section id={step.id} class="guide-step">
						<div class="step-heading">
							<span class="step-number" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
							<h2>{step.title}</h2>
						</div>
						{#each step.body as paragraph}<p>{paragraph}</p>{/each}
						{#each screenReferences.filter((item) => item.step === step.id) as screenshot}
							<GuideImage {...screenshot} directory="slate-help" />
						{/each}
						<GuideImage
							src={`${guide.slug}-${step.id}.webp`}
							directory="slate-help"
							alt={`${step.visual.title}. ${step.visual.note} ${step.visual.callouts.join(' ')}`}
							caption={`${guide.status ? 'Availability overview' : `Step ${i + 1}`}: ${step.visual.title}. Illustrated example using sample data.`}
						/>
						<p class="step-result">
							<i class="bx bx-check-circle" aria-hidden="true"></i><span
								><strong>Expected result:</strong> {step.outcome}</span
							>
						</p>
					</section>
				{/each}
				<section id="troubleshooting" class="troubleshooting">
					<h2>{guide.status ? 'Common question' : 'Troubleshooting'}</h2>
					{#each guide.troubleshooting as item}<details>
							<summary>{item.question}</summary>
							<p>{item.answer}</p>
						</details>{/each}
				</section>
				<aside class="help-support">
					<strong>Still need assistance?</strong>
					<p>
						Email <a href="mailto:support@d-cine.net">support@d-cine.net</a> with the tool name, relevant
						film or booking identity and the exact error message. Keep private offer terms and recipient
						details out of public reports.
					</p>
				</aside>
			</div>
		</div>
	</article>
	<nav class="related-guides" aria-label="Related walkthroughs">
		<h2>Continue with another guide</h2>
		<div>
			{#each guides.filter((item) => item.slug !== guide.slug) as item}<a
					href={`${base}/slate/help/${item.slug}/`}
					>{item.title}<i class="bx bx-right-arrow-alt" aria-hidden="true"></i></a
				>{/each}
		</div>
	</nav>
</main>
